import { useState, useEffect, useCallback, useRef } from "react";
import { menuAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import {
  UtensilsCrossed, Plus, Trash2, ChevronDown, RefreshCw,
  Loader2, DollarSign, Upload, FileText, Image, Sparkles,
  Check, X, Brain, Lightbulb, AlertCircle, Plug, Clock
} from "lucide-react";

export default function MenuPage() {
  const { isPremium } = useAuth();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [openCategories, setOpenCategories] = useState({});

  // AI Upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [parsedMenu, setParsedMenu] = useState(null);
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // AI Suggestions state
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Add item/category dialogs
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [addItemCatId, setAddItemCatId] = useState("");
  const [newItemName, setNewItemName] = useState("");
  const [newItemPrice, setNewItemPrice] = useState("");
  const [newItemDesc, setNewItemDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");

  // Sync status
  const [syncStatus, setSyncStatus] = useState(null);

  const fetchMenu = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, itemRes, statusRes] = await Promise.all([
        menuAPI.getCategories(), menuAPI.getItems(), menuAPI.syncStatus()
      ]);
      setCategories(catRes.data);
      setItems(itemRes.data);
      setSyncStatus(statusRes.data);
      const openState = {};
      catRes.data.forEach(c => { openState[c.id] = true; });
      setOpenCategories(openState);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  // ──── AI Upload Handlers ────
  const handleFileUpload = async (file) => {
    if (!file) return;
    const validTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a PDF, PNG, JPG, or WEBP file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }
    setUploading(true);
    setParsedMenu(null);
    try {
      const res = await menuAPI.aiParse(file);
      setParsedMenu(res.data.parsed_menu);
      toast.success(`Menu parsed from ${res.data.source}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse menu");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const handleImportParsedMenu = async () => {
    if (!parsedMenu?.categories) return;
    setImporting(true);
    try {
      const res = await menuAPI.aiImport(parsedMenu);
      toast.success(`Imported ${res.data.categories} categories, ${res.data.items} items!`);
      setUploadDialogOpen(false);
      setParsedMenu(null);
      await fetchMenu();
    } catch (err) {
      toast.error("Failed to import menu");
    } finally {
      setImporting(false);
    }
  };

  // ──── AI Suggestions ────
  const handleGetSuggestions = async () => {
    setLoadingSuggestions(true);
    setSuggestions(null);
    try {
      const res = await menuAPI.aiSuggestions();
      setSuggestions(res.data);
      setSuggestionsOpen(true);
    } catch (err) {
      toast.error("Failed to get AI suggestions");
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // ──── Standard Handlers ────
  const handleSync = async () => {
    setSyncing(true);
    try {
      await menuAPI.syncPOS();
      await fetchMenu();
      toast.success("Menu synced from POS!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to sync menu"); }
    finally { setSyncing(false); }
  };

  const handleToggleAvailability = async (item) => {
    try {
      await menuAPI.updateItem(item.id, { available: !item.available });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, available: !i.available } : i));
    } catch (err) { toast.error("Failed to update item"); }
  };

  const handleAddItem = async () => {
    if (!newItemName || !newItemPrice) { toast.error("Name and price required"); return; }
    setSaving(true);
    try {
      await menuAPI.createItem({ category_id: addItemCatId, name: newItemName, price: parseFloat(newItemPrice), description: newItemDesc });
      await fetchMenu();
      setAddItemOpen(false);
      setNewItemName(""); setNewItemPrice(""); setNewItemDesc("");
      toast.success("Item added!");
    } catch (err) { toast.error("Failed to add item"); }
    finally { setSaving(false); }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await menuAPI.deleteItem(itemId);
      setItems(prev => prev.filter(i => i.id !== itemId));
      toast.success("Item deleted");
    } catch (err) { toast.error("Failed to delete item"); }
  };

  const handleAddCategory = async () => {
    if (!newCatName) { toast.error("Category name required"); return; }
    setSaving(true);
    try {
      await menuAPI.createCategory({ name: newCatName, sort_order: categories.length });
      await fetchMenu();
      setAddCatOpen(false);
      setNewCatName("");
      toast.success("Category added!");
    } catch (err) { toast.error("Failed to add category"); }
    finally { setSaving(false); }
  };

  const handleDeleteCategory = async (catId) => {
    try {
      await menuAPI.deleteCategory(catId);
      setCategories(prev => prev.filter(c => c.id !== catId));
      setItems(prev => prev.filter(i => i.category_id !== catId));
      toast.success("Category deleted");
    } catch (err) { toast.error("Failed to delete category"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const SUGGESTION_TYPE_ICONS = {
    missing_description: { icon: FileText, color: "text-blue-600", bg: "bg-blue-50" },
    upsell_pairing: { icon: Lightbulb, color: "text-amber-600", bg: "bg-amber-50" },
    modifier_suggestion: { icon: Plus, color: "text-emerald-600", bg: "bg-emerald-50" },
    pricing: { icon: DollarSign, color: "text-violet-600", bg: "bg-violet-50" },
  };

  return (
    <div className="space-y-5 fade-in" data-testid="menu-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Menu</h1>
          <p className="text-sm text-slate-500 mt-0.5">{items.length} items across {categories.length} categories</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isPremium && (
          <Button variant="outline" size="sm" onClick={handleGetSuggestions} disabled={loadingSuggestions || items.length === 0} className="h-9 text-xs" data-testid="ai-suggestions-button">
            {loadingSuggestions ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5 text-amber-500" />}
            AI Suggestions
          </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} className="h-9 text-xs" data-testid="menu-sync-pos-button">
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Sync POS
          </Button>
          <Button size="sm" onClick={() => setUploadDialogOpen(true)} className="h-9 text-xs shadow-sm shadow-primary/20" data-testid="ai-upload-menu-button">
            <Brain className="w-3.5 h-3.5 mr-1.5" />
            AI Upload Menu
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAddCatOpen(true)} className="h-9 text-xs" data-testid="add-category-button">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Category
          </Button>
        </div>
      </div>

      {/* Sync Status Indicator */}
      {syncStatus && (
        <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-slate-100 bg-white" data-testid="sync-status-bar">
          <div className="flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${syncStatus.connected ? "bg-green-500 pulse-green" : "bg-red-400"}`} />
            <div className="flex items-center gap-2">
              <Plug className={`w-3.5 h-3.5 ${syncStatus.connected ? "text-green-600" : "text-red-400"}`} />
              <span className="text-[13px] font-medium text-slate-700">
                {syncStatus.connected
                  ? `${(syncStatus.pos_type || "pos").charAt(0).toUpperCase() + (syncStatus.pos_type || "pos").slice(1)} Connected`
                  : "POS Not Connected"}
              </span>
            </div>
            {syncStatus.last_sync && (
              <>
                <span className="text-slate-200">|</span>
                <div className="flex items-center gap-1.5 text-[12px] text-slate-400">
                  <Clock className="w-3 h-3" />
                  <span>Last synced {new Date(syncStatus.last_sync).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })}</span>
                </div>
              </>
            )}
            {syncStatus.last_sync_result && (
              <>
                <span className="text-slate-200">|</span>
                <span className="text-[12px] text-slate-400">
                  {syncStatus.last_sync_result.categories} categories, {syncStatus.last_sync_result.items} items
                </span>
              </>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing || !syncStatus.connected} className="h-8 text-[11px]" data-testid="sync-now-button">
            {syncing ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <RefreshCw className="w-3 h-3 mr-1.5" />}
            Sync Now
          </Button>
        </div>
      )}

      {/* Menu Categories */}
      {categories.length === 0 ? (
        <Card className="border-slate-100 border-dashed" data-testid="menu-empty-state">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/5 flex items-center justify-center mx-auto mb-4">
              <UtensilsCrossed className="w-7 h-7 text-primary/60" />
            </div>
            <p className="text-base font-semibold text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>No menu items yet</p>
            <p className="text-sm text-slate-400 mt-1 mb-5 max-w-sm mx-auto">Upload a PDF or photo of your menu and AI will parse it automatically, or sync from POS.</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => setUploadDialogOpen(true)} className="shadow-sm shadow-primary/20" data-testid="menu-empty-upload">
                <Brain className="w-4 h-4 mr-2" /> AI Upload Menu
              </Button>
              <Button variant="outline" onClick={handleSync} data-testid="menu-empty-sync">
                <RefreshCw className="w-4 h-4 mr-2" /> Sync from POS
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {categories.map(cat => {
            const catItems = items.filter(i => i.category_id === cat.id);
            return (
              <Collapsible key={cat.id} open={openCategories[cat.id]} onOpenChange={(open) => setOpenCategories(prev => ({ ...prev, [cat.id]: open }))}>
                <Card className="border-slate-100" data-testid={`menu-category-${cat.id}`}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-slate-50/50 transition-colors flex flex-row items-center justify-between py-3.5 px-5">
                      <div className="flex items-center gap-2.5">
                        <CardTitle className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>{cat.name}</CardTitle>
                        <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-500">{catItems.length}</Badge>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.id); }} data-testid={`delete-category-${cat.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${openCategories[cat.id] ? "rotate-180" : ""}`} />
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0 pb-4 px-5">
                      <div className="space-y-1.5">
                        {catItems.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`menu-item-${item.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-[13px] font-medium text-slate-800">{item.name}</span>
                                <span className="text-[13px] text-slate-500 tabular-nums">${item.price?.toFixed(2)}</span>
                              </div>
                              {item.description && <p className="text-[11px] text-slate-400 mt-0.5 truncate">{item.description}</p>}
                              {item.modifiers?.length > 0 && (
                                <div className="flex gap-1 mt-1">
                                  {item.modifiers.map((mod, i) => (
                                    <Badge key={i} variant="outline" className="text-[9px] px-1.5 py-0 text-slate-400 border-slate-200">{mod.group}: {mod.options?.length || 0}</Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2.5 ml-4">
                              <Switch checked={item.available} onCheckedChange={() => handleToggleAvailability(item)} data-testid={`toggle-item-${item.id}`} />
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" onClick={() => handleDeleteItem(item.id)} data-testid={`delete-item-${item.id}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        <Button variant="ghost" size="sm" className="w-full border border-dashed border-slate-200 text-slate-400 hover:text-primary hover:border-primary/30 h-9 text-xs" onClick={() => { setAddItemCatId(cat.id); setAddItemOpen(true); }} data-testid={`add-item-to-${cat.id}`}>
                          <Plus className="w-3 h-3 mr-1" /> Add Item
                        </Button>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
        </div>
      )}

      {/* ──── AI Upload Dialog ──── */}
      <Dialog open={uploadDialogOpen} onOpenChange={(open) => { setUploadDialogOpen(open); if (!open) setParsedMenu(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="ai-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Brain className="w-5 h-5 text-primary" /> AI Menu Parser
            </DialogTitle>
          </DialogHeader>

          {!parsedMenu ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Upload a PDF or photo of your menu. AI will extract all items, prices, categories, and modifiers automatically.</p>
              {/* Drop zone */}
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                  dragOver ? "border-primary bg-primary/5" : "border-slate-200 hover:border-primary/40"
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="menu-upload-dropzone"
              >
                <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => handleFileUpload(e.target.files[0])} data-testid="menu-file-input" />
                {uploading ? (
                  <div className="py-4">
                    <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-3" />
                    <p className="text-sm font-medium text-slate-700">AI is parsing your menu...</p>
                    <p className="text-xs text-slate-400 mt-1">This may take 15-30 seconds</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-blue-500" />
                      </div>
                      <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                        <Image className="w-5 h-5 text-emerald-500" />
                      </div>
                    </div>
                    <p className="text-sm font-medium text-slate-700">Drop your menu file here, or click to browse</p>
                    <p className="text-xs text-slate-400 mt-1">Supports PDF, PNG, JPG, WEBP (max 10MB)</p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1 min-h-0 space-y-4">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <Check className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">
                  Parsed {parsedMenu.categories?.length || 0} categories, {parsedMenu.categories?.reduce((sum, c) => sum + (c.items?.length || 0), 0) || 0} items
                </span>
              </div>
              <ScrollArea className="h-[350px]">
                <div className="space-y-4 pr-2">
                  {parsedMenu.categories?.map((cat, ci) => (
                    <div key={ci} className="border rounded-xl p-4 border-slate-100">
                      <h4 className="text-sm font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{cat.name}</h4>
                      <div className="space-y-1.5">
                        {cat.items?.map((item, ii) => (
                          <div key={ii} className="flex items-start justify-between py-1.5 px-2 rounded-lg hover:bg-slate-50">
                            <div className="flex-1 min-w-0">
                              <span className="text-[13px] font-medium text-slate-700">{item.name}</span>
                              {item.description && <p className="text-[11px] text-slate-400 mt-0.5">{item.description}</p>}
                              {item.modifiers?.length > 0 && (
                                <div className="flex gap-1 mt-1 flex-wrap">
                                  {item.modifiers.map((mod, mi) => (
                                    <Badge key={mi} variant="outline" className="text-[9px] px-1.5 py-0 text-slate-400">{mod.group} ({mod.options?.length})</Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            <span className="text-[13px] font-semibold text-slate-800 tabular-nums ml-3">${parseFloat(item.price || 0).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <AlertCircle className="w-3 h-3" />
                <span>Review the parsed menu above. Click Import to add all items to your menu.</span>
              </div>
            </div>
          )}

          <DialogFooter>
            {parsedMenu ? (
              <div className="flex gap-2 w-full">
                <Button variant="secondary" className="flex-1" onClick={() => setParsedMenu(null)} data-testid="menu-parse-retry">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload Different File
                </Button>
                <Button className="flex-1 shadow-sm shadow-primary/20" onClick={handleImportParsedMenu} disabled={importing} data-testid="menu-import-button">
                  {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
                  Import {parsedMenu.categories?.reduce((s, c) => s + (c.items?.length || 0), 0)} Items
                </Button>
              </div>
            ) : (
              <Button variant="secondary" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──── AI Suggestions Dialog ──── */}
      <Dialog open={suggestionsOpen} onOpenChange={setSuggestionsOpen}>
        <DialogContent className="max-w-lg" data-testid="ai-suggestions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Sparkles className="w-5 h-5 text-amber-500" /> AI Menu Suggestions
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-3 pr-2">
              {suggestions?.suggestions?.map((s, i) => {
                const typeInfo = SUGGESTION_TYPE_ICONS[s.type] || SUGGESTION_TYPE_ICONS.missing_description;
                const Icon = typeInfo.icon;
                return (
                  <div key={i} className="p-3 rounded-xl border border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg ${typeInfo.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                        <Icon className={`w-4 h-4 ${typeInfo.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-slate-800">{s.suggestion}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">{s.reason}</p>
                        {s.item_name && <Badge variant="outline" className="text-[9px] mt-1.5 text-slate-400">{s.item_name}</Badge>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* ──── Add Item Dialog ──── */}
      <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
        <DialogContent data-testid="add-item-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Add Menu Item</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="text-sm">Name *</Label><Input placeholder="Pepperoni Pizza" value={newItemName} onChange={(e) => setNewItemName(e.target.value)} data-testid="new-item-name" /></div>
            <div className="space-y-1.5"><Label className="text-sm">Price *</Label>
              <div className="relative"><DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input type="number" step="0.01" placeholder="12.99" value={newItemPrice} onChange={(e) => setNewItemPrice(e.target.value)} className="pl-8" data-testid="new-item-price" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-sm">Description</Label><Input placeholder="Classic pepperoni with mozzarella" value={newItemDesc} onChange={(e) => setNewItemDesc(e.target.value)} data-testid="new-item-description" /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setAddItemOpen(false)}>Cancel</Button>
            <Button onClick={handleAddItem} disabled={saving} data-testid="save-new-item">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null} Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──── Add Category Dialog ──── */}
      <Dialog open={addCatOpen} onOpenChange={setAddCatOpen}>
        <DialogContent data-testid="add-category-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Add Category</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="text-sm">Category Name *</Label><Input placeholder="Salads" value={newCatName} onChange={(e) => setNewCatName(e.target.value)} data-testid="new-category-name" /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setAddCatOpen(false)}>Cancel</Button>
            <Button onClick={handleAddCategory} disabled={saving} data-testid="save-new-category">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null} Add Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
